import {
  Box,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  InputRightElement,
  Tooltip,
  CloseButton,
  FormErrorIcon,
} from '@chakra-ui/react';
import { t } from '@transifex/native';
import { T } from '@transifex/react';
import { useMemo, useCallback, ChangeEvent, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { getTableOptions } from './membersTableHelpers';
import { useMemberGuards } from 'features/members/utils/lib';
import { useFeature } from 'lib/featureFlags/features';
import { allowedSearchCharactersRegex } from 'features/common/validations/matchers';
import { EmptyTablePlaceholder } from './components/EmptyTablePlaceHolder';
import { mdiMagnify } from '@mdi/js';
import styled from '@emotion/styled';
import {
  DataTable,
  DataTableListOptions,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { adminPath, membersPath } from 'features/admin/components/admin-paths';
import { useMembersData } from '../../hooks/useMembersData/useMembersData';
import { withErrorBoundary } from 'features/common/utils/errorBoundaries';
import { MembersErrorPage } from 'features/main/components/CustomErrorPages/MembersErrorPage';

const TableDiv = styled.div`
  table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

export type IMembersTableProps = RouteComponentProps<{ page: string }>;

export const MembersTable = (props: IMembersTableProps) => {
  const { allowMemberAccess } = useMemberGuards();
  const isUserGroupsEnabled = useFeature('USER_GROUPS');
  // Manage sort state
  const [sortBy, setSortBy] = useState<DataTableSortingRule[]>([
    { id: 'lastLogin', desc: true }, // Default sort by last login descending
  ]);

  const {
    memberToShow,
    totalCount,
    membersLoading,
    pageGroupsLoading,
    pageGroupsNeeded,
    searchFilter,
    setSearchFilter,
    searchStringIsValid,
    setSearchStringIsValid,
    page,
    pageSize,
    pagination,
    setPage,
    history,
  } = useMembersData(props, sortBy);

  const options = useMemo(
    () =>
      getTableOptions({
        page,
        allowMemberAccess,
        showGroupsColumn: isUserGroupsEnabled,
      }),
    [page, allowMemberAccess, isUserGroupsEnabled],
  );

  const onChange = useCallback(
    (options: DataTableListOptions) => {
      setPage(options.page);
      // Update sort state when user clicks on column headers
      if (options.sortBy) {
        setSortBy(options.sortBy);
      }
      history.push({
        pathname: `${adminPath}${membersPath}/page/${options.page}`,
        search: history.location.search,
      });
    },
    [history, setPage],
  );

  // Keep the search bar mounted during all loading states.
  // Only the table body should transition to a loading skeleton.
  const isTableLoading =
    membersLoading || pageGroupsLoading || pageGroupsNeeded;

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchInputValue = event.target.value;

    if (allowedSearchCharactersRegex.test(searchInputValue)) {
      setSearchFilter(searchInputValue);
      setSearchStringIsValid(true);
    } else {
      setSearchStringIsValid(false);
    }
  };

  const onClearHandler = () => {
    setSearchFilter('');
    setSearchStringIsValid(true);
  };

  const onClickRow = useCallback(
    (row: { id: string }) => {
      history.push({
        pathname: `${adminPath}${membersPath}/page/${page}/details/${row.id}`,
        search: history.location.search,
      });
    },
    [history, page],
  );

  return (
    <>
      <Flex justifyContent='space-between' marginBottom={12} maxW='xs'>
        <FormControl isInvalid={!searchStringIsValid}>
          <InputGroup mb='4' maxWidth='sm'>
            <InputLeftElement
              pointerEvents='none'
              // TODO change this after we open Input, InputElement props
              children={<PortalIcon path={mdiMagnify} />}
            />
            <Input
              placeholder='Search'
              aria-label='Search members'
              data-testid='member_search_input'
              data-behavior-analytics-feature='members search'
              onChange={onSearch}
              value={searchFilter}
              role='search'
            />
            {searchFilter.length > 0 && (
              <InputRightElement>
                <Tooltip label='Clear search'>
                  <CloseButton
                    onClick={onClearHandler}
                    data-testid='member_search_clear_button'
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>
            <FormErrorIcon />
            <T _str='Currently, the search is only available in alphanumeric characters, at signs (@), dots, dashes, and underscores.' />
          </FormErrorMessage>
        </FormControl>
      </Flex>

      {!isTableLoading && memberToShow.length === 0 ? (
        <EmptyTablePlaceholder
          description={t('Change your search parameters and try again.')}
          title={t('No users found')}
        />
      ) : (
        <TableDiv>
          <Box pointerEvents={isTableLoading ? 'none' : 'auto'}>
            <DataTable
              columns={options.columns}
              rowActions={options.rowActions}
              pagination={pagination as 'hidden' | 'complex'}
              totalItems={totalCount}
              data={isTableLoading ? [] : memberToShow}
              onChange={onChange}
              onClickRow={onClickRow}
              page={page}
              pageSize={pageSize}
              isLoading={isTableLoading}
              sortBy={sortBy}
              // IMPORTANT: Members sorting is handled outside of DataTable (server-side for most columns,
              // client-side for Groups across the full dataset). Keep react-table sorting manual so it
              // doesn't re-sort the already-sorted data (especially Groups which renders React nodes).
              autoSort={false}
              theaderProps={{ 'data-behavior-analytics-id': 'members sorting' }}
              paginationProps={{
                'data-behavior-analytics-feature': ' members pagination',
              }}
              data-behavior-analytics-feature='members table'
              regionLabel='Members table'
            />
          </Box>
        </TableDiv>
      )}
    </>
  );
};

export const MembersTableWithErrorHandling = withErrorBoundary(
  MembersTable,
  MembersErrorPage,
);
